import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {Box, Button, GlobalStyles, TextField, Typography} from '@mui/material';
import {keyframes} from '@mui/material/styles';
import {cpcGovLoginService, isCpcGovLoggedIn, landingRouteAfterLogin} from '../../../services/cpcGov.service';
import GalanoRegular from '../../../assets/fonts/cpc/GalanoGrotesque-Regular.otf';
import GalanoBold from '../../../assets/fonts/cpc/GalanoGrotesque-Bold.otf';
import CpcHeader from '../components/CpcHeader';
import CpcFooter from '../components/CpcFooter';

/**
 * CPC Government Dashboard — sign in.
 *
 * THERE IS NO SIGN-UP LINK, deliberately. Accounts are pre-created, one per
 * CPCI country, because country_code is the security scope for the whole
 * dashboard — a reviewer choosing their own country would defeat the isolation
 * model. Credentials are issued to the country's focal point out of band.
 */

const C = {
    // navy/navyDeep/white stay literal — see the matching comment in
    // NationalOverview.page.jsx. Plain text/background roles use the
    // dedicated keys below, which flip via --cpc-* (see darkMode.css).
    navy: '#003C69',
    navyDeep: '#003D69',
    navyDark: '#002855',
    cyan: '#0096C8',
    cyanDeep: '#007DB4',
    white: '#FFFFFF',
    mist: 'var(--cpc-surface-subtle, #F5F8FB)',
    muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))',
    required: '#007DB4',
    disabled: '#c5d0da',
    error: '#C0553A',
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const fontFaceStyles = {
    '@font-face': [
        {fontFamily: 'GalanoGrotesque', fontWeight: 400, src: `url(${GalanoRegular}) format("opentype")`, fontDisplay: 'swap'},
        {fontFamily: 'GalanoGrotesque', fontWeight: 700, src: `url(${GalanoBold}) format("opentype")`, fontDisplay: 'swap'},
    ],
};

const cpcRise = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: none; }
`;

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        background: C.mist,
        fontSize: 15,
        fontFamily: 'inherit',
        color: C.navyDeep,
        '& fieldset': {borderColor: 'rgba(0,60,105,.2)'},
        '&:hover fieldset': {borderColor: 'rgba(0,60,105,.2)'},
        '&.Mui-focused': {background: C.white},
        '&.Mui-focused fieldset': {borderColor: C.cyanDeep, borderWidth: 1, boxShadow: '0 0 0 3px rgba(0,150,200,.15)'},
    },
    '& .MuiOutlinedInput-input::placeholder': {color: 'var(--cpc-text-muted, rgba(0,60,105,.45))', opacity: 1},
};

const FieldLabel = ({children, required}) => (
    <Typography component="span" sx={{fontSize: 15, fontWeight: 700, color: C.text, mb: 0.75, display: 'block'}}>
        {children} {required && <Box component="span" sx={{color: C.required}}>*</Box>}
    </Typography>
);

const LoginPage = () => {
    const history = useHistory();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        document.title = 'Government Dashboard — Sign in';
        // Already signed in? Send them wherever they actually belong rather than
        // showing a sign-in form to someone who is signed in.
        if (!isCpcGovLoggedIn()) return undefined;

        let alive = true;
        landingRouteAfterLogin().then((route) => {
            if (alive) history.replace(route);
        });
        return () => { alive = false; };
    }, [history]);

    const valid = !!(email.trim() && password);

    const submit = async (e) => {
        e.preventDefault();
        if (!valid || busy) return;

        setBusy(true);
        setError('');
        try {
            await cpcGovLoginService(email.trim(), password);
            // No setBusy(false) on this path: the push unmounts this page, and
            // releasing the button afterwards is a state update on a component
            // that no longer exists. Staying "Signing in…" until it disappears
            // is also the honest thing to show — the work really is still going.
            history.push(await landingRouteAfterLogin());
        } catch (err) {
            setError(err?.message || 'Sign in failed. Please check your details and try again.');
            setBusy(false);
        }
    };

    return (
        <Box
            className="cpc-gov-scope"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'GalanoGrotesque, "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: C.text,
                background: C.mist,
                WebkitFontSmoothing: 'antialiased',
            }}
        >
            <GlobalStyles styles={fontFaceStyles}/>

            <CpcHeader/>

            <Box
                component="main"
                sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '56px 28px 80px',
                    animation: `${cpcRise} .45s ease both`,
                    '@media (max-width: 720px)': {padding: '36px 18px 64px'},
                }}
            >
                <Box sx={{width: '100%', maxWidth: 460}}>
                    <Typography component="h1" sx={{margin: '0 0 12px', fontSize: 'clamp(26px, 3.5vw, 36px)', lineHeight: 1.15, fontWeight: 700, color: C.text}}>
                        Sign in
                    </Typography>
                    <Typography sx={{margin: '0 0 28px', fontSize: 17, lineHeight: 1.5, fontWeight: 600, color: C.text}}>
                        Use the credentials issued to your country&rsquo;s focal point.
                    </Typography>

                    <Box
                        component="form"
                        onSubmit={submit}
                        noValidate
                        sx={{background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))', padding: '32px 30px', boxShadow: '0 8px 28px rgba(0,40,85,.06)', '@media (max-width: 720px)': {padding: '24px 18px'}}}
                    >
                        <Box sx={{mb: 2.25}}>
                            <FieldLabel required>Email address</FieldLabel>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                type="email"
                                autoComplete="username"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError('');
                                }}
                                placeholder="name@gov.example"
                                sx={fieldSx}
                            />
                        </Box>

                        <Box>
                            <FieldLabel required>Password</FieldLabel>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                type="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setError('');
                                }}
                                placeholder="Enter your password"
                                sx={fieldSx}
                            />
                        </Box>

                        {error && (
                            <Typography sx={{mt: 2, fontSize: 14, fontWeight: 700, color: C.error}}>
                                {error}
                            </Typography>
                        )}

                        <Button
                            type="submit"
                            disabled={!valid || busy}
                            fullWidth
                            sx={{
                                mt: 3,
                                border: '2px solid',
                                borderColor: valid && !busy ? C.navy : C.disabled,
                                background: valid && !busy ? C.navy : C.disabled,
                                color: C.white,
                                fontFamily: 'inherit',
                                fontSize: 16,
                                fontWeight: 700,
                                lineHeight: 1.2,
                                padding: '14px 26px',
                                borderRadius: 0,
                                textTransform: 'none',
                                boxShadow: valid && !busy ? '0 4px 6px rgba(0,0,0,.1)' : 'none',
                                transition: 'background .2s ease, border-color .2s ease',
                                '&:hover': {
                                    background: valid && !busy ? C.navyDark : C.disabled,
                                    borderColor: valid && !busy ? C.navyDark : C.disabled,
                                },
                                '&.Mui-disabled': {
                                    color: C.white,
                                    background: C.disabled,
                                    borderColor: C.disabled,
                                },
                            }}
                        >
                            {busy ? 'Signing in…' : 'Sign in'}
                        </Button>

                        <Typography sx={{mt: 2, fontSize: 14, lineHeight: 1.5, color: C.muted}}>
                            Accounts are issued by the CPC Index team, one per participating country.
                            If you need access, contact your national focal point.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <CpcFooter/>
        </Box>
    );
};

export default LoginPage;
